import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { AnimatePresence } from "framer-motion";
import { Hero } from "../components/Hero";
import { ResultCard } from "../components/ResultCard";
import { HowItWorks } from "../components/HowItWorks";
import { ZodiacGrid } from "../components/ZodiacGrid";
import { ApiRateLimitModal } from "../components/ApiRateLimitModal";
import { useHoroscope } from "../hooks/useHoroscope";
import { getZodiacSign } from "../utils/zodiac";
import "./Home.css";

export const Home = () => {
  const { horoscope, loading, error, generateHoroscopeForSign, reset, isApiRateLimit, retryCount } = useHoroscope();
  const [timeframe, setTimeframe] = useState("TODAY");
  const [showApiRateLimitModal, setShowApiRateLimitModal] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastGenerationParams, setLastGenerationParams] = useState(null);

  // JSON-LD structured data for horoscope
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "WTFHoroscope",
    "url": "https://wtfhoroscope.com",
    "description": "AI-generated brutal horoscopes for entertainment",
    "applicationCategory": "Entertainment",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const handleGenerateHoroscope = async (dateOfBirth, selectedTimeframe, name, profession) => {
    setTimeframe(selectedTimeframe);
    setLastGenerationParams({ dateOfBirth, selectedTimeframe, name, profession });
    
    try {
      await generateHoroscopeForSign(dateOfBirth, selectedTimeframe, name, profession);
      // Scroll to result card
      setTimeout(() => {
        const resultSection = document.querySelector(".result-section");
        if (resultSection) {
          resultSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } catch (err) {
      console.error("Error generating horoscope:", err);
    }
  };

  // Show API rate limit modal when rate limit error occurs
  useEffect(() => {
    if (isApiRateLimit && error === "RATE_LIMIT_ERROR") {
      setShowApiRateLimitModal(true);
    }
  }, [isApiRateLimit, error]);

  const handleApiRateLimitRetry = async () => {
    if (!lastGenerationParams) return;

    setIsRetrying(true);
    try {
      const { dateOfBirth, selectedTimeframe, name, profession } = lastGenerationParams;
      await generateHoroscopeForSign(dateOfBirth, selectedTimeframe, name, profession);
      setShowApiRateLimitModal(false);
      setIsRetrying(false);
      
      // Scroll to result card
      setTimeout(() => {
        const resultSection = document.querySelector(".result-section");
        if (resultSection) {
          resultSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } catch (err) {
      setIsRetrying(false);
      console.error("Retry failed:", err);
    }
  };

  const handleZodiacClick = async (sign) => {
    // Scroll to hero input area and focus on name input
    setTimeout(() => {
      const heroSection = document.querySelector(".hero");
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: "smooth" });
      }
      // Focus on name input field
      const nameInput = document.querySelector(".name-input");
      if (nameInput) {
        nameInput.focus();
      }
    }, 100);
  };

  const handleTryAnother = () => {
    reset();
  };

  return (
    <>
      <Helmet>
        <title>WTFHoroscope - Brutally Honest Daily Horoscope | Free AI Horoscope</title>
        <meta name="description" content="Get your brutally honest AI horoscope. No fake positivity, just cosmic truth. Daily, monthly and yearly predictions for all zodiac signs. Free forever." />
        <meta name="keywords" content="horoscope, astrology, zodiac, AI horoscope, free horoscope, daily horoscope, funny horoscope" />
        
        {/* Open Graph */}
        <meta property="og:title" content="WTFHoroscope - Brutally Honest Daily Horoscope | Free AI Horoscope" />
        <meta property="og:description" content="Get your brutally honest AI horoscope. No fake positivity, just cosmic truth." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wtfhoroscope.com" />
        <meta property="og:image" content="https://wtfhoroscope.com/og-image.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="WTFHoroscope - Brutally Honest Daily Horoscope" />
        <meta name="twitter:description" content="No fake positivity, just cosmic truth. AI-generated horoscopes for all zodiac signs." />
        <meta name="twitter:image" content="https://wtfhoroscope.com/og-image.png" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://wtfhoroscope.com" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <main className="home">
        <Hero onGenerate={handleGenerateHoroscope} isLoading={loading} />
        
        {horoscope && (
          <ResultCard
            horoscope={horoscope}
            onTryAnother={handleTryAnother}
            loading={loading}
            error={error}
          />
        )}

        {error && (
          <ResultCard
            horoscope={null}
            onTryAnother={handleTryAnother}
            loading={loading}
            error={error}
          />
        )}

        <HowItWorks onZodiacSelect={handleZodiacClick} />
        
        <ZodiacGrid onSignClick={handleZodiacClick} loading={loading} />
      </main>

      {/* API Rate Limit Modal */}
      <AnimatePresence>
        {showApiRateLimitModal && (
          <ApiRateLimitModal
            onRetry={handleApiRateLimitRetry}
            isRetrying={isRetrying}
            attemptCount={retryCount}
          />
        )}
      </AnimatePresence>
    </>
  );
};
